import React from 'react';
import { Controller, useFormContext } from 'react-hook-form';
import { TextField, SxProps, Theme } from '@mui/material';
import { styled } from '@mui/material/styles';
import { Text } from 'src/components/BeaconV2Components/Generic/Text';
import Flex from 'src/components/BeaconV2Components/Flex/Flex';
import color from 'src/utils/colors';

const StyledTextField = styled(TextField)({
  '& .MuiInputBase-root': {
    borderRadius: '4px',
    border: `1px solid ${color.graysSecondary}`,
    backgroundColor: 'white',
    fontSize: '14px',
    padding: '10px 14px',
    paddingRight: '8px', // Adjusted to provide 8px spacing from scrollbar to container edge
    boxSizing: 'border-box',
    minHeight: '48px',
    '&:hover': {
      borderColor: color.graysSecondary
    },
    '& .MuiInputBase-input': {
      padding: '0px',
      paddingRight: '6px', // Space between text and scrollbar
      '&::placeholder': {
        color: '#BEC7D3', // #7E8FA8 at 50% opacity
        opacity: 1
      }
    }
  },
  '& .MuiInputBase-input.Mui-disabled, & .MuiInputBase-inputMultiline.Mui-disabled': {
    WebkitTextFillColor: color.blueGrey,
    color: color.blueGrey,
    opacity: 1
  },
  '& .MuiOutlinedInput-notchedOutline': {
    border: 'none'
  }
});

interface StyledMultilineFieldProps {
  name: string;
  label?: string;
  placeholder?: string;
  rows?: number;
  isKeywordField?: boolean;
  disabled?: boolean;
  sx?: SxProps<Theme>;
}

export const StyledMultilineField: React.FC<StyledMultilineFieldProps> = ({
  name,
  label,
  placeholder,
  rows = 4,
  isKeywordField = false,
  disabled = false,
  sx
}) => {
  const { control } = useFormContext();

  return (
    <Controller
      name={name}
      control={control}
      render={({ field }) => (
        <Flex flexDirection="column" sx={{ width: '100%' }}>
          {label && (
            <Text variant="h7" sx={{ fontWeight: 500, marginBottom: '8px' }}>
              {label}
            </Text>
          )}
          <StyledTextField
            multiline
            rows={rows}
            placeholder={placeholder}
            disabled={disabled}
            value={
              isKeywordField
                ? Array.isArray(field.value)
                  ? field.value.join(', ')
                  : field.value || ''
                : field.value || ''
            }
            onChange={(e) => {
              if (isKeywordField) {
                const keywords = e.target.value
                  .split(',')
                  .map((k) => k.trim())
                  .filter((k) => k);
                field.onChange(keywords);
              } else {
                field.onChange(e.target.value);
              }
            }}
            fullWidth
            sx={{
              // WebKit (Chrome, Edge, Safari)
              '& .MuiInputBase-inputMultiline::-webkit-scrollbar, & .MuiOutlinedInput-inputMultiline::-webkit-scrollbar, & textarea::-webkit-scrollbar': {
                width: '5px',
              },
              '& .MuiInputBase-inputMultiline::-webkit-scrollbar-thumb, & .MuiOutlinedInput-inputMultiline::-webkit-scrollbar-thumb, & textarea::-webkit-scrollbar-thumb': {
                backgroundColor: '#BEC7D3', // #7E8FA8 at 50% opacity
                borderRadius: '4px',
              },
              ...(sx as object)
            }}
          />
        </Flex>
      )}
    />
  );
};
